// src/components/admin/QRScannerModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';

function QRScannerModal({ onClose }) {
  const navigate    = useNavigate();
  const { salonId } = useParams();

  // Camera refs
  const qrCodeRef  = useRef(null);
  const startedRef = useRef(false);

  // File upload ref
  const fileInputRef = useRef(null);

  // 'camera' | 'upload'
  const [scanMode, setScanMode] = useState('camera');

  // 'scanning' | 'validating' | 'valid' | 'error'
  const [phase, setPhase] = useState('scanning');

  const [errorMsg, setErrorMsg]             = useState('');
  const [scannedVoucher, setScannedVoucher] = useState(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [uploadFileName, setUploadFileName]     = useState('');

  // ── Camera: start ──────────────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');

      let cameras = [];
      try {
        cameras = await Html5Qrcode.getCameras();
      } catch (enumErr) {
        throw enumErr;
      }

      if (!cameras || cameras.length === 0) {
        throw Object.assign(new Error('No cameras found'), { name: 'NotFoundError' });
      }

      const backCamera = cameras.find(c => {
        const label = (c.label || '').toLowerCase();
        return label.includes('back') || label.includes('rear') || label.includes('environment');
      });
      const selectedCamera = backCamera || cameras[0];

      const instance = new Html5Qrcode('bb-qr-reader');
      qrCodeRef.current = instance;

      await instance.start(
        selectedCamera.id,
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decodedText) => {
          await stopCamera();
          handleDecodedQR(decodedText);
        },
        () => {}
      );

    } catch (err) {
      console.error('Camera start error:', err);
      const name = err?.name || '';
      const msg2 = err?.message || '';
      let msg = 'Could not start camera. ';

      if (name === 'NotAllowedError' || name === 'PermissionDeniedError' ||
          msg2.toLowerCase().includes('permission')) {
        msg += 'Camera permission was denied. Click the camera icon in your browser address bar, set it to "Allow", then try again.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError' ||
                 msg2.toLowerCase().includes('no cameras')) {
        msg += 'No camera was detected on this device.';
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        msg += 'The camera is already in use by another app or tab. Close it and try again.';
      } else {
        msg += msg2 || 'Please allow camera access in your browser and try again.';
      }

      setErrorMsg(msg);
      setPhase('error');
    }
  };

  // ── Camera: stop ───────────────────────────────────────────────────────────
  const stopCamera = async () => {
    const instance = qrCodeRef.current;
    if (!instance) return;
    try {
      const state = instance.getState();
      if (state === 2 || state === 3) await instance.stop();
      instance.clear();
    } catch (_) {}
    qrCodeRef.current = null;
  };

  // ── Mount / unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startCamera();
    return () => { stopCamera(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mode switching ─────────────────────────────────────────────────────────
  const handleModeSwitch = async (newMode) => {
    if (newMode === scanMode) return;

    // Reset to idle state when switching modes
    setPhase('scanning');
    setErrorMsg('');
    setScannedVoucher(null);
    setUploadFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (newMode === 'upload') {
      // Stop camera before switching to upload
      await stopCamera();
      startedRef.current = false;
    } else {
      // Start camera when switching back to camera tab
      startedRef.current = true;
      startCamera();
    }

    setScanMode(newMode);
  };

  // ── File upload scan ───────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFileName(file.name);
    setIsProcessingFile(true);
    setErrorMsg('');

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      // scanFile() uses the element only as a constructor ID — it doesn't
      // render anything visible, so reusing bb-qr-reader is fine here.
      const instance    = new Html5Qrcode('bb-qr-reader');
      const decodedText = await instance.scanFile(file, /* showImage */ false);
      instance.clear();

      setIsProcessingFile(false);
      handleDecodedQR(decodedText);

    } catch (err) {
      console.error('File scan error:', err);
      setIsProcessingFile(false);
      setUploadFileName('');
      setErrorMsg('No QR code could be read from this image. Make sure the image is clear and contains the full QR code, then try again.');
      setPhase('error');
    }

    // Reset input so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Decode & validate ──────────────────────────────────────────────────────
  const handleDecodedQR = async (rawText) => {
    setPhase('validating');

    let parsed;
    try {
      parsed = JSON.parse(rawText);
    } catch {
      setErrorMsg('Invalid QR code — not a BespokeBeauty voucher.');
      setPhase('error');
      return;
    }

    const { voucherId, customerId, salonId: qrSalonId } = parsed;

    if (!voucherId || !customerId || !qrSalonId) {
      setErrorMsg('Invalid QR code — missing required voucher fields.');
      setPhase('error');
      return;
    }

    if (qrSalonId !== salonId) {
      setErrorMsg('This voucher belongs to a different salon.');
      setPhase('error');
      return;
    }

    try {
      const voucherSnap = await getDoc(doc(db, 'vouchers', voucherId));

      if (!voucherSnap.exists()) {
        setErrorMsg('Voucher not found.');
        setPhase('error');
        return;
      }

      const voucher = { id: voucherSnap.id, ...voucherSnap.data() };

      if (voucher.status === 'redeemed') {
        setErrorMsg('This voucher has already been redeemed.');
        setPhase('error');
        return;
      }
      if (voucher.status === 'inactive') {
        setErrorMsg('This voucher is inactive and cannot be redeemed.');
        setPhase('error');
        return;
      }

      const now    = new Date();
      const toDate = (ts) =>
        ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts);

      if (voucher.startDate && now < toDate(voucher.startDate)) {
        setErrorMsg(`This voucher is not yet valid. It starts on ${toDate(voucher.startDate).toLocaleDateString()}.`);
        setPhase('error');
        return;
      }
      if (voucher.endDate && now > toDate(voucher.endDate)) {
        setErrorMsg(`This voucher expired on ${toDate(voucher.endDate).toLocaleDateString()}.`);
        setPhase('error');
        return;
      }

      setScannedVoucher({ voucher, customerId });
      setPhase('valid');

    } catch (err) {
      console.error('Voucher validation error:', err);
      setErrorMsg('Failed to validate voucher. Please try again.');
      setPhase('error');
    }
  };

  // ── Proceed to customer page ───────────────────────────────────────────────
  const handleProceed = () => {
    const { voucher, customerId } = scannedVoucher;
    onClose();
    navigate(
      `/BespokeBeauty/${salonId}/admin/customers/${customerId}`,
      { state: { pendingVoucher: voucher } }
    );
  };

  // ── Retry ──────────────────────────────────────────────────────────────────
  const handleRetry = async () => {
    setPhase('scanning');
    setErrorMsg('');
    setScannedVoucher(null);
    setUploadFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (scanMode === 'camera') {
      await stopCamera();
      startedRef.current = false;
      setTimeout(() => {
        startedRef.current = true;
        startCamera();
      }, 50);
    }
    // Upload mode just resets the UI — user picks a new file manually
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const voucherTypeLabel = (type) => {
    switch (type) {
      case 'percentage':   return 'Percentage Discount';
      case 'fixed_amount': return 'Fixed Amount Off';
      case 'points_based': return 'Points Based Redemption';
      case 'free_gift':    return 'Free Gift';
      default:             return type;
    }
  };

  const discountSummary = (v) => {
    if (v.type === 'percentage')   return `${v.discountValue}% off`;
    if (v.type === 'fixed_amount') return `$${v.discountValue} off`;
    if (v.type === 'points_based') return `$${v.voucherAmount} credit (${v.pointsRequired?.toLocaleString()} pts)`;
    if (v.type === 'free_gift')    return v.freeGiftDescription || 'Free Gift';
    return '';
  };

  const isIdle = phase === 'scanning'; // true = show the main input UI

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <h3 style={styles.title}>Scan Voucher QR Code</h3>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Mode tabs ── */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(scanMode === 'camera' ? styles.tabActive : {}) }}
            onClick={() => handleModeSwitch('camera')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ marginRight: 6, flexShrink: 0 }}>
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
            Use Camera
          </button>
          <button
            style={{ ...styles.tab, ...(scanMode === 'upload' ? styles.tabActive : {}) }}
            onClick={() => handleModeSwitch('upload')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ marginRight: 6, flexShrink: 0 }}>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            Upload Image
          </button>
        </div>

        {/* ── Body ── */}
        <div style={styles.body}>

          {/* Camera feed — always in DOM; hidden when not in camera/scanning */}
          <div
            id="bb-qr-reader"
            style={{
              width: '100%',
              ...(scanMode === 'camera' && isIdle
                ? {}
                : { height: 0, overflow: 'hidden', visibility: 'hidden' })
            }}
          />

          {/* ── Upload mode idle UI ── */}
          {scanMode === 'upload' && isIdle && (
            <div style={styles.centreBlock}>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />

              {isProcessingFile ? (
                /* Processing spinner */
                <>
                  <div style={styles.spinner} />
                  <p style={styles.statusText}>Reading QR code from image…</p>
                  {uploadFileName && (
                    <p style={{ ...styles.statusText, fontSize: '12px', color: '#aaa' }}>
                      {uploadFileName}
                    </p>
                  )}
                </>
              ) : (
                /* Upload zone */
                <>
                  <div
                    style={styles.uploadZone}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {/* Image / QR icon */}
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
                      stroke="#c8bfb6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <p style={styles.uploadTitle}>Choose an image to scan</p>
                    <p style={styles.uploadSub}>
                      Select a screenshot or photo of the customer's voucher QR code
                      from your gallery, downloads, or WhatsApp
                    </p>
                    <button
                      style={styles.chooseBtn}
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    >
                      Browse Gallery / Photos
                    </button>
                  </div>
                  <p style={styles.uploadHint}>
                    Supported: PNG, JPG, WEBP, HEIC and most image formats
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── Validating ── */}
          {phase === 'validating' && (
            <div style={styles.centreBlock}>
              <div style={styles.spinner} />
              <p style={styles.statusText}>Validating voucher…</p>
            </div>
          )}

          {/* ── Error ── */}
          {phase === 'error' && (
            <div style={styles.centreBlock}>
              <div style={styles.errorIcon}>✕</div>
              <p style={styles.errorText}>{errorMsg}</p>
              {scanMode === 'camera' ? (
                <button style={styles.retryBtn} onClick={handleRetry}>
                  Try Again
                </button>
              ) : (
                <button
                  style={styles.retryBtn}
                  onClick={() => {
                    setPhase('scanning');
                    setErrorMsg('');
                    setUploadFileName('');
                  }}
                >
                  Choose Another Image
                </button>
              )}
            </div>
          )}

          {/* ── Valid ── */}
          {phase === 'valid' && scannedVoucher && (
            <div style={styles.centreBlock}>
              <div style={styles.successIcon}>✓</div>
              <p style={styles.successText}>Voucher Valid</p>

              <div style={styles.voucherCard}>
                <p style={styles.voucherName}>{scannedVoucher.voucher.promotionName}</p>
                <p style={styles.voucherType}>{voucherTypeLabel(scannedVoucher.voucher.type)}</p>
                <p style={styles.voucherDiscount}>{discountSummary(scannedVoucher.voucher)}</p>
                <p style={styles.voucherCode}>Code: {scannedVoucher.voucher.voucherCode}</p>
              </div>

              <div style={styles.actionRow}>
                <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
                <button style={styles.proceedBtn} onClick={handleProceed}>
                  Open Transaction →
                </button>
              </div>
            </div>
          )}

        </div>

        {/* ── Hint footer ── */}
        {isIdle && scanMode === 'camera' && (
          <p style={styles.hint}>
            Point your camera at the customer's voucher QR code on their phone.
          </p>
        )}

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    background: '#fff', borderRadius: '12px',
    width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    display: 'flex', flexDirection: 'column'
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid #f0ece8'
  },
  title:    { margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a2e' },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '22px', color: '#888', lineHeight: 1, padding: '0 4px'
  },

  // ── Tabs ──
  tabs: {
    display: 'flex', borderBottom: '1px solid #f0ece8',
    padding: '0 20px', gap: '4px'
  },
  tab: {
    display: 'flex', alignItems: 'center',
    padding: '10px 14px', fontSize: '13px', fontWeight: 500,
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    cursor: 'pointer', color: '#888', marginBottom: '-1px',
    transition: 'color 0.15s, border-color 0.15s'
  },
  tabActive: {
    color: '#1a1a2e', borderBottomColor: '#1a1a2e'
  },

  // ── Body ──
  body: {
    padding: '20px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px'
  },
  centreBlock: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: '12px', width: '100%', textAlign: 'center'
  },

  // ── Upload zone ──
  uploadZone: {
    width: '100%', padding: '28px 20px',
    border: '2px dashed #e0d8d0', borderRadius: '12px',
    background: '#faf8f6', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
    transition: 'border-color 0.15s'
  },
  uploadTitle: { margin: 0, fontSize: '14px', fontWeight: 600, color: '#1a1a2e' },
  uploadSub: {
    margin: 0, fontSize: '12px', color: '#999', maxWidth: '280px', lineHeight: 1.5
  },
  chooseBtn: {
    marginTop: '4px', padding: '9px 20px', borderRadius: '8px',
    border: 'none', background: '#1a1a2e',
    cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 600
  },
  uploadHint: {
    margin: 0, fontSize: '11px', color: '#bbb', textAlign: 'center'
  },

  // ── Spinner ──
  spinner: {
    width: '40px', height: '40px',
    border: '3px solid #f0ece8', borderTop: '3px solid #b6a593',
    borderRadius: '50%', animation: 'bb-spin 0.8s linear infinite'
  },
  statusText: { color: '#555', fontSize: '14px', margin: 0 },

  // ── Error / Success ──
  errorIcon: {
    width: '52px', height: '52px', borderRadius: '50%',
    background: '#fee2e2', color: '#dc2626',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '24px', fontWeight: 700
  },
  errorText: { color: '#dc2626', fontSize: '14px', margin: 0, maxWidth: '300px' },
  successIcon: {
    width: '52px', height: '52px', borderRadius: '50%',
    background: '#dcfce7', color: '#16a34a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '24px', fontWeight: 700
  },
  successText: { color: '#16a34a', fontSize: '15px', fontWeight: 600, margin: 0 },

  // ── Voucher card ──
  voucherCard: {
    background: '#faf8f6', border: '1px solid #e8e0d8',
    borderRadius: '8px', padding: '14px 18px', width: '100%', textAlign: 'left'
  },
  voucherName:     { margin: '0 0 4px', fontWeight: 600, fontSize: '15px', color: '#1a1a2e' },
  voucherType:     { margin: '0 0 4px', fontSize: '12px', color: '#888' },
  voucherDiscount: { margin: '0 0 6px', fontSize: '14px', color: '#b6a593', fontWeight: 600 },
  voucherCode:     { margin: 0, fontSize: '12px', color: '#aaa', fontFamily: 'monospace' },

  // ── Actions ──
  actionRow: { display: 'flex', gap: '10px', width: '100%', marginTop: '4px' },
  cancelBtn: {
    flex: 1, padding: '10px', borderRadius: '8px',
    border: '1px solid #ddd', background: '#fff',
    cursor: 'pointer', fontSize: '14px', color: '#555'
  },
  proceedBtn: {
    flex: 2, padding: '10px', borderRadius: '8px',
    border: 'none', background: '#1a1a2e',
    cursor: 'pointer', fontSize: '14px', color: '#fff', fontWeight: 600
  },
  retryBtn: {
    padding: '10px 24px', borderRadius: '8px',
    border: 'none', background: '#1a1a2e',
    cursor: 'pointer', fontSize: '14px', color: '#fff', fontWeight: 600
  },
  hint: {
    fontSize: '12px', color: '#999', textAlign: 'center',
    padding: '0 20px 16px', margin: 0
  }
};

// Inject spinner keyframe once
if (typeof document !== 'undefined' && !document.getElementById('bb-qr-keyframe')) {
  const s = document.createElement('style');
  s.id = 'bb-qr-keyframe';
  s.textContent = '@keyframes bb-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

export default QRScannerModal;
